import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RangeSlider } from '../Slider';
import Input from '../Input';
import { clampRange, isRangeActive } from './filterLogic';

const sameRange = (a, b) => a[0] === b[0] && a[1] === b[1];

/**
 * FilterRange — a range attribute (price) in a Filter panel or the drawer
 * (internal): the DS RangeSlider plus From / To number fields.
 *
 * Commits — `onCommit([lo, hi])` — only when a thumb is released or a field
 * is left / confirmed with Enter, never on every drag tick: committing
 * re-filters the grid. A typed "to" below "from" swaps them; values are
 * clamped into the attribute's bounds; an unchanged range is not committed.
 */
const FilterRange = ({ attribute, value, onCommit, labels, formatValue }) => {
  const bounds = [attribute.min, attribute.max];
  const current = isRangeActive(attribute, value) ? clampRange(attribute, value) : bounds;
  const currentKey = `${current[0]}:${current[1]}`;

  const [draft, setDraft] = useState(current);
  const [text, setText] = useState(current.map(String));

  // The applied value moved (a chip removed, the URL changed): follow it
  useEffect(() => {
    setDraft(current);
    setText(current.map(String));
  }, [currentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (range) => {
    const next = clampRange(attribute, range);
    setDraft(next);
    setText(next.map(String));
    if (!sameRange(next, current)) onCommit(next);
  };

  const commitField = (index) => {
    const typed = Number(String(text[index]).replace(',', '.'));
    if (text[index] === '' || !Number.isFinite(typed)) {
      setText(draft.map(String));
      return;
    }
    const next = [...draft];
    next[index] = typed;
    commit(next);
  };

  const fieldProps = (index) => ({
    type: 'number',
    showIcon: false,
    value: text[index],
    onChange: (e) => {
      const v = e.target.value;
      setText((t) => (index === 0 ? [v, t[1]] : [t[0], v]));
    },
    onBlur: () => commitField(index),
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitField(index);
      }
    },
    inputMode: 'numeric',
    min: attribute.min,
    max: attribute.max,
    step: attribute.step,
  });

  return (
    <div className="ds-filter-range">
      <RangeSlider
        label={attribute.label}
        value={draft}
        min={attribute.min}
        max={attribute.max}
        step={attribute.step || 1}
        formatValue={(v) => formatValue(v, attribute)}
        minLabel={labels.rangeMin(attribute.label)}
        maxLabel={labels.rangeMax(attribute.label)}
        onChange={(next) => {
          setDraft(next);
          setText(next.map(String));
        }}
        onChangeEnd={commit}
      />
      <div className="ds-filter-range__fields">
        <Input label={labels.from} {...fieldProps(0)} />
        <Input label={labels.to} {...fieldProps(1)} />
      </div>
    </div>
  );
};

FilterRange.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number,
    unit: PropTypes.string,
  }).isRequired,
  value: PropTypes.arrayOf(PropTypes.number),
  onCommit: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  formatValue: PropTypes.func.isRequired,
};

export default FilterRange;
